import React,{useState,useMemo} from 'react';
import ReactFlow,{Background,Controls,MiniMap} from 'reactflow';
import Icon from '../Text/Icon';
import Subheading from '../Text/Subheading';
import Text from '../Text/Text';
import ButtonPrimary from '../Buttons/ButtonPrimary';

/**
 * RearrangePreview - Shows before/after preview of layout changes
 */
export default function RearrangePreview({
    originalNodes,
    newPositions,
    layout,
    onApply,
    onReject
}) {
    const [showBefore,setShowBefore]=useState(false);

    // Create preview nodes with new positions
    const previewNodes=useMemo(() => {
        if(!newPositions||!originalNodes) return originalNodes||[];

        return originalNodes.map(node => {
            const newPos=newPositions.find(p => p.id===node.id);
            return {
                ...node,
                position: newPos? newPos.position:node.position,
                // Add visual indicator for repositioned nodes
                style: {
                    ...node.style,
                    opacity: newPos? 1:0.5
                }
            };
        });
    },[originalNodes,newPositions]);

    const displayNodes=showBefore? originalNodes:previewNodes;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-2xl w-[90%] h-[90%] max-w-6xl flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <Icon icon="arrow-repeat" className="text-2xl text-purple-600" />
                        <div>
                            <Subheading className="!pb-0">
                                Layout Preview: {layout||'Hierarchical'}
                            </Subheading>
                            <Text className="text-xs text-gray-500">
                                Review the new layout before applying
                            </Text>
                        </div>
                    </div>

                    {/* Toggle Before/After */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setShowBefore(!showBefore)}
                            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${showBefore
                                    ? 'bg-gray-200 text-gray-800'
                                    :'bg-purple-100 text-purple-700'
                                }`}
                        >
                            {showBefore? 'Showing: Original':'Showing: New Layout'}
                        </button>
                    </div>
                </div>

                {/* Preview Canvas */}
                <div className="flex-1 relative">
                    <ReactFlow
                        nodes={displayNodes}
                        edges={[]} // Don't show edges in preview to reduce clutter
                        fitView
                        fitViewOptions={{
                            padding: 0.2,
                            maxZoom: 1
                        }}
                        nodesDraggable={false}
                        nodesConnectable={false}
                        elementsSelectable={false}
                        zoomOnScroll={false}
                        panOnScroll={false}
                        preventScrolling={false}
                    >
                        <Background />
                        <Controls showInteractive={false} />
                        <MiniMap
                            nodeStrokeWidth={3}
                            zoomable={false}
                            pannable={false}
                        />
                    </ReactFlow>

                    {/* Layout Info Overlay */}
                    <div className="absolute top-4 left-4 bg-white bg-opacity-90 rounded-lg shadow-md p-3 max-w-xs">
                        <Text className="text-xs font-semibold text-gray-700 mb-2">
                            Layout Information
                        </Text>
                        <div className="space-y-1 text-xs text-gray-600">
                            <div className="flex justify-between">
                                <span>Algorithm:</span>
                                <span className="font-semibold">{layout||'Hierarchical'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Nodes repositioned:</span>
                                <span className="font-semibold">{newPositions?.length||0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Total nodes:</span>
                                <span className="font-semibold">{originalNodes?.length||0}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center gap-2">
                        <Icon icon="info-circle" className="text-blue-500" />
                        <Text className="text-xs text-gray-600">
                            Toggle between original and new layout to compare
                        </Text>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={onReject}
                            className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors font-semibold text-sm"
                        >
                            Cancel
                        </button>
                        <ButtonPrimary
                            onClick={onApply}
                            className="!px-6 !py-2"
                        >
                            <div className="flex items-center gap-2">
                                <Icon icon="check" />
                                Apply Layout
                            </div>
                        </ButtonPrimary>
                    </div>
                </div>
            </div>
        </div>
    );
}
